import { useEffect, useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Button } from "./ui/button";

const b64ToBytes = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone;

export function PushCard() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) return;
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then((s) => setOn(!!s)).catch(() => {});
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      if (Notification.permission === "denied") throw new Error("Notifications are blocked. Allow them in your browser or site settings.");
      if ((await Notification.requestPermission()) !== "granted") throw new Error("Permission not granted");
      const { data } = await api.get("/push/vapid-key");
      const reg = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) ||
        (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.key) }));
      await api.post("/push/subscribe", sub.toJSON());
      await api.post("/push/test");
      setOn(true);
      toast.success("Lock-screen notifications enabled");
    } catch (e) { toast.error(e?.response ? errText(e) : e.message); } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      setOn(false);
      toast.success("Push notifications turned off on this device");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const iosHint = isIOS() && !standalone();
  return (
    <div className="surface rounded-2xl p-5" data-testid="push-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gold/15 text-gold flex items-center justify-center shrink-0"><BellRing size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">Lock-screen notifications</div>
          <p className="text-xs text-muted-foreground mt-0.5">Get likes, messages, follows and event reminders even when VirtuStars is closed.</p>
          {iosHint && <p className="text-[11px] text-amber-400 mt-2" data-testid="push-ios-hint">On iPhone: tap Share → Add to Home Screen, open VirtuStars from your Home Screen, then enable here (iOS 16.4+).</p>}
          {!supported() && !iosHint && <p className="text-[11px] text-amber-400 mt-2" data-testid="push-unsupported">This browser doesn't support push notifications.</p>}
        </div>
        {supported() && (
          <Button onClick={on ? disable : enable} disabled={busy} data-testid="push-toggle-btn"
            className={`rounded-full shrink-0 ${on ? "btn-gold text-black" : "btn-blue"}`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : on ? "On" : "Enable"}
          </Button>
        )}
      </div>
    </div>
  );
}
